import { useState, useCallback, useEffect } from 'react'
import type { ProgramTaskData } from '../../types/program-scheduling.types'
import type { Video } from '../../types/database.types'
import { VideoSelector } from './VideoSelector'
import { VideoUploadModal } from './VideoUploadModal'
import { VideoPlayerModal } from '../video'
import { Button } from '../common'
import { videoService } from '../../services'
import { useAuthStore } from '../../stores'
import { muxConfig } from '../../config/mux.config'

interface TaskSchedulerStepProps {
  tasks: ProgramTaskData[]
  onChange: (tasks: ProgramTaskData[]) => void
}

interface TaskEditorModalProps {
  isOpen: boolean
  task: ProgramTaskData | null
  onSave: (task: ProgramTaskData) => void
  onClose: () => void
}

function TaskEditorModal({
  isOpen,
  task,
  onSave,
  onClose,
}: TaskEditorModalProps) {
  const [editedTask, setEditedTask] = useState<Partial<ProgramTaskData>>({})

  useEffect(() => {
    if (task) {
      setEditedTask(task)
    } else {
      setEditedTask({
        exerciseName: '',
        notes: '',
        sets: undefined,
        reps: undefined,
        durationSeconds: undefined,
        restSeconds: undefined,
      })
    }
  }, [task])

  if (!isOpen) return null

  const handleSave = () => {
    if (!editedTask.videoId) return
    onSave({
      ...editedTask,
      exerciseName: editedTask.exerciseName || 'Exercise',
      videoId: editedTask.videoId,
    } as ProgramTaskData)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {task?.id ? 'Edit Exercise' : 'Add Exercise Details'}
        </h3>

        <div className="space-y-4">
          {/* Exercise Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Exercise Name
            </label>
            <input
              type="text"
              value={editedTask.exerciseName || ''}
              onChange={(e) =>
                setEditedTask({ ...editedTask, exerciseName: e.target.value })
              }
              placeholder="e.g., Back Squats"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Sets and Reps */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sets
              </label>
              <input
                type="number"
                min={1}
                value={editedTask.sets || ''}
                onChange={(e) =>
                  setEditedTask({
                    ...editedTask,
                    sets: parseInt(e.target.value) || undefined,
                  })
                }
                placeholder="e.g., 3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reps
              </label>
              <input
                type="number"
                min={1}
                value={editedTask.reps || ''}
                onChange={(e) =>
                  setEditedTask({
                    ...editedTask,
                    reps: parseInt(e.target.value) || undefined,
                  })
                }
                placeholder="e.g., 10"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={editedTask.notes || ''}
              onChange={(e) =>
                setEditedTask({ ...editedTask, notes: e.target.value })
              }
              placeholder="Any special instructions..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} fullWidth>
            Cancel
          </Button>
          <Button onClick={handleSave} fullWidth>
            {task?.id ? 'Save Changes' : 'Add Exercise'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export function TaskSchedulerStep({
  tasks,
  onChange,
}: TaskSchedulerStepProps) {
  const { canUploadVideos } = useAuthStore()
  const [isVideoSelectorOpen, setIsVideoSelectorOpen] = useState(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isTaskEditorOpen, setIsTaskEditorOpen] = useState(false)
  const [editingTask, setEditingTask] = useState<ProgramTaskData | null>(null)
  const [pendingVideoId, setPendingVideoId] = useState<string | null>(null)
  const [pendingVideoTitle, setPendingVideoTitle] = useState<string | null>(null)
  const [videoCache, setVideoCache] = useState<Record<string, Video>>({})
  const [previewVideo, setPreviewVideo] = useState<Video | null>(null)

  // Load video details for tasks
  useEffect(() => {
    const loadVideoDetails = async () => {
      const videoIds = tasks.map((t) => t.videoId).filter((id) => !videoCache[id])
      if (videoIds.length === 0) return

      for (const id of videoIds) {
        try {
          const video = await videoService.getVideoById(id)
          if (video) {
            setVideoCache((prev) => ({ ...prev, [id]: video }))
          }
        } catch (e) {
          console.error('Failed to load video:', e)
        }
      }
    }
    loadVideoDetails()
  }, [tasks])

  const [pendingVideo, setPendingVideo] = useState<Video | null>(null)

  const handleVideoSelect = useCallback((video: Video) => {
    setPendingVideoId(video.id)
    setPendingVideoTitle(video.title)
    setPendingVideo(video)
    // Also add to videoCache immediately so thumbnail shows
    setVideoCache((prev) => ({ ...prev, [video.id]: video }))
    setIsVideoSelectorOpen(false)
    setEditingTask(null)
    setIsTaskEditorOpen(true)
  }, [])

  // Handle upload complete - auto-open task editor with uploaded video
  const handleUploadComplete = useCallback((videoId: string, title: string) => {
    setPendingVideoId(videoId)
    setPendingVideoTitle(title)
    setIsUploadModalOpen(false)
    setEditingTask(null)
    setIsTaskEditorOpen(true)
  }, [])

  const handleTaskSave = useCallback(
    (task: ProgramTaskData) => {
      // Get the video data from pendingVideo or videoCache
      const videoData = pendingVideo || videoCache[task.videoId || pendingVideoId!]

      const taskWithVideo: ProgramTaskData = {
        ...task,
        videoId: task.videoId || pendingVideoId!,
        id: task.id || `task-${Date.now()}`,
        // Include video object for immediate thumbnail display (with mux_playback_id and thumbnail_time for correct thumbnails)
        video: videoData ? {
          id: videoData.id,
          title: videoData.title,
          thumbnail_url: videoData.thumbnail_url,
          duration_seconds: videoData.duration_seconds,
          mux_playback_id: videoData.mux_playback_id,
          thumbnail_time: videoData.thumbnail_time,
        } : task.video,
      }

      if (editingTask?.id) {
        // Update existing
        onChange(tasks.map((t) => (t.id === editingTask.id ? taskWithVideo : t)))
      } else {
        // Add new
        onChange([...tasks, taskWithVideo])
      }

      setPendingVideoId(null)
      setPendingVideo(null)
      setEditingTask(null)
      setIsTaskEditorOpen(false)
    },
    [tasks, onChange, editingTask, pendingVideoId, pendingVideo, videoCache]
  )

  const handleEditTask = useCallback((task: ProgramTaskData) => {
    setEditingTask(task)
    setIsTaskEditorOpen(true)
  }, [])

  const handleDeleteTask = useCallback(
    (taskId: string) => {
      onChange(tasks.filter((t) => t.id !== taskId))
    },
    [tasks, onChange]
  )

  const formatDuration = (seconds: number | null | undefined) => {
    if (!seconds) return '--:--'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600">
            Add exercises to your program. Each exercise is linked to a video from your library.
          </p>
        </div>
        <div className="flex gap-2">
          {canUploadVideos() && (
            <Button variant="outline" onClick={() => setIsUploadModalOpen(true)}>
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              Upload Video
            </Button>
          )}
          <Button onClick={() => setIsVideoSelectorOpen(true)}>
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Exercise
          </Button>
        </div>
      </div>

      {/* Tasks List */}
      {tasks.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <svg
            className="w-12 h-12 mx-auto text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No exercises yet
          </h3>
          <p className="text-gray-500 mb-4">
            Click "Add Exercise" to select a video from your library
          </p>
          <Button variant="outline" onClick={() => setIsVideoSelectorOpen(true)}>
            Add Your First Exercise
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {tasks.map((task, index) => {
            // Use task.video (from edit mode) or videoCache (from newly added)
            const video = task.video || videoCache[task.videoId]
            const videoDuration = video?.duration_seconds || task.video?.duration_seconds
            // Generate thumbnail URL - use mux_playback_id with thumbnail_time for the user-selected frame
            const cachedVideo = videoCache[task.videoId] as Video | undefined
            const muxPlaybackId = video?.mux_playback_id || cachedVideo?.mux_playback_id
            const thumbnailTime = video?.thumbnail_time ?? cachedVideo?.thumbnail_time
            const thumbnailUrl = muxPlaybackId
              ? muxConfig.getThumbnailUrl(muxPlaybackId, {
                  width: 160,
                  height: 96,
                  time: thumbnailTime ?? (videoDuration ? videoDuration * 0.25 : 0),
                })
              : video?.thumbnail_url

            return (
              <div
                key={task.id || index}
                className="flex items-center gap-4 bg-white p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
              >
                {/* Order Number */}
                <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-medium text-sm flex-shrink-0">
                  {index + 1}
                </div>

                {/* Video Thumbnail - Clickable to preview */}
                <button
                  onClick={() => {
                    const videoToPreview = task.video || videoCache[task.videoId]
                    if (videoToPreview) {
                      setPreviewVideo(videoToPreview as Video)
                    }
                  }}
                  className="w-16 h-10 bg-gray-200 rounded overflow-hidden flex-shrink-0 relative group cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
                  title="Click to preview video"
                >
                  {thumbnailUrl ? (
                    <>
                      <img
                        src={thumbnailUrl}
                        alt={task.exerciseName}
                        className="w-full h-full object-cover"
                      />
                      {/* Play overlay */}
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg
                          className="w-5 h-5 text-white"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}
                </button>

                {/* Task Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 truncate">
                    {task.exerciseName}
                  </h4>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    {videoDuration && (
                      <span>{formatDuration(videoDuration)}</span>
                    )}
                    {task.sets && task.reps && (
                      <>
                        <span>·</span>
                        <span>
                          {task.sets}×{task.reps}
                        </span>
                      </>
                    )}
                    {task.notes && (
                      <>
                        <span>·</span>
                        <span className="truncate">{task.notes}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEditTask(task)}
                    className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50"
                    title="Edit"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => task.id && handleDeleteTask(task.id)}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                    title="Delete"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Summary */}
      {tasks.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Program Summary</h4>
          <div className="grid grid-cols-2 gap-4 text-sm text-blue-700">
            <div>
              <span className="font-semibold">{tasks.length}</span> exercise{tasks.length !== 1 ? 's' : ''}
            </div>
            <div>
              <span className="font-semibold">
                {formatDuration(
                  tasks.reduce((sum, t) => {
                    // Use task.video (from edit mode) or videoCache (from newly added)
                    const video = t.video || videoCache[t.videoId]
                    return sum + (video?.duration_seconds || 0)
                  }, 0)
                )}
              </span>{' '}
              total video time
            </div>
          </div>
        </div>
      )}

      {/* Video Selector Modal */}
      <VideoSelector
        isOpen={isVideoSelectorOpen}
        onClose={() => setIsVideoSelectorOpen(false)}
        onSelect={handleVideoSelect}
        excludeVideoIds={[]}
      />

      {/* Task Editor Modal */}
      <TaskEditorModal
        isOpen={isTaskEditorOpen}
        task={editingTask ? editingTask : pendingVideoId ? { videoId: pendingVideoId, exerciseName: pendingVideoTitle || '' } as ProgramTaskData : null}
        onSave={handleTaskSave}
        onClose={() => {
          setIsTaskEditorOpen(false)
          setPendingVideoId(null)
          setPendingVideoTitle(null)
          setPendingVideo(null)
          setEditingTask(null)
        }}
      />

      {/* Video Upload Modal */}
      <VideoUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadComplete={handleUploadComplete}
      />

      {/* Video Preview Modal */}
      <VideoPlayerModal
        isOpen={!!previewVideo}
        video={previewVideo}
        onClose={() => setPreviewVideo(null)}
      />
    </div>
  )
}
